/* SHOP ALL — filtering, sorting and paging for sections/shop-all.liquid.

   Everything runs against markup Liquid already rendered: the section prints every
   product once, each carrying the haystack the facets were counted from, so a filter
   is a class change rather than a round trip. That is also why the sidebar counts and
   the results can never disagree — both are read off the same list. */

(function () {
  'use strict';

  var CATEGORY = 'category';
  var BRAND = 'brand';

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDecimals = Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (!format) return '$' + withCommas;

    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas);
  }

  function ShopAll(root) {
    this.root = root;
    this.section = root.closest('.shop-all__section') || root.parentNode;

    var configNode = this.section.querySelector('[data-shop-all-config]');
    this.config = configNode ? JSON.parse(configNode.textContent) : {};
    this.batchSize = this.config.batchSize || 16;

    this.grid = root.querySelector('[data-grid]');
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-item]'));
    this.featured = root.querySelector('[data-featured]');
    this.chipsRow = root.querySelector('[data-chips]');
    this.emptyMessage = this.section.querySelector('[data-empty]');
    this.moreWrapper = this.section.querySelector('.shop-all__more');
    this.facets = root.querySelector('.shop-all__facets');

    this.chipTemplate = this.section.querySelector('[data-chip-template]');
    this.chipClearTemplate = this.section.querySelector('[data-chip-clear-template]');

    this.priceMin = root.querySelector('[data-price-min]');
    this.priceMax = root.querySelector('[data-price-max]');
    this.priceFill = root.querySelector('[data-price-fill]');
    this.priceReadoutMin = root.querySelector('[data-price-readout-min]');
    this.priceReadoutMax = root.querySelector('[data-price-readout-max]');

    // Every item keeps its printed order so "Featured" can always sort back to it.
    this.items.forEach(function (item, index) {
      item.dataset.originalIndex = String(index);
    });

    this.state = {
      category: '',
      brand: '',
      sizes: [],
      priceMin: this.priceMin ? Number(this.priceMin.min) : 0,
      priceMax: this.priceMax ? Number(this.priceMax.max) : Infinity,
      sort: 'manual',
      shown: this.batchSize
    };

    this.bind();
    this.collapseSizes();
    this.updatePriceUI();
    this.apply();
  }

  ShopAll.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      /* Matched by group rather than by tag: the same data-facet also sits on the size
         checkboxes, and those must keep their native behaviour. Category and brand
         arrive either as a sidebar button or as a pill from the button snippet, which
         wraps it in an anchor — hence the preventDefault. */
      var option = event.target.closest('[data-facet="' + CATEGORY + '"], [data-facet="' + BRAND + '"]');
      if (option) {
        event.preventDefault();
        self.selectFacet(option);
        return;
      }

      var seeAll = event.target.closest('[data-see-all]');
      if (seeAll) {
        self.toggleSizes(seeAll);
        return;
      }

      var loadMore = event.target.closest('[data-load-more]');
      if (loadMore) {
        // The snippet renders the button inside an anchor, which would navigate.
        event.preventDefault();
        self.state.shown += self.batchSize;
        self.render();
        return;
      }

      var chip = event.target.closest('.filter-chip');
      if (chip) {
        self.handleChip(chip);
        return;
      }

      var filterToggle = event.target.closest('.shop-all__filter-toggle .dropdown__trigger');
      if (filterToggle) {
        self.toggleDrawer(true);
        return;
      }

      if (event.target.closest('[data-facets-close]')) {
        self.toggleDrawer(false);
      }
    });

    this.root.addEventListener('change', function (event) {
      var checkbox = event.target.closest('.checkbox__input');
      if (checkbox && checkbox.name === 'size') {
        self.state.sizes = Array.prototype.slice
          .call(self.root.querySelectorAll('.checkbox__input[name="size"]:checked'))
          .map(function (input) { return input.value; });
        self.state.shown = self.batchSize;
        self.apply();
      }
    });

    if (this.priceMin && this.priceMax) {
      [this.priceMin, this.priceMax].forEach(function (input) {
        input.addEventListener('input', function () {
          // Stop the handles crossing — past each other the range inverts and the
          // filter silently matches nothing.
          var min = Number(self.priceMin.value);
          var max = Number(self.priceMax.value);
          if (min > max) {
            if (input === self.priceMin) self.priceMin.value = String(max);
            else self.priceMax.value = String(min);
          }
          self.state.priceMin = Number(self.priceMin.value);
          self.state.priceMax = Number(self.priceMax.value);
          self.state.shown = self.batchSize;
          self.updatePriceUI();
          self.apply();
        });
      });
    }

    this.bindSort();

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.facets && self.facets.classList.contains('is-open')) {
        self.toggleDrawer(false);
      }
    });
  };

  ShopAll.prototype.bindSort = function () {
    var self = this;
    var sort = this.root.querySelector('.shop-all__sort');
    if (!sort) return;

    var trigger = sort.querySelector('.dropdown__trigger');
    var menu = sort.querySelector('.dropdown__menu');
    var label = sort.querySelector('[data-dropdown-label]');
    if (!trigger || !menu) return;

    trigger.addEventListener('click', function () {
      var open = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
      menu.hidden = open;
    });

    menu.addEventListener('click', function (event) {
      var option = event.target.closest('.dropdown__option');
      if (!option) return;

      menu.querySelectorAll('.dropdown__option').forEach(function (node) {
        node.setAttribute('aria-selected', String(node === option));
      });
      if (label) label.textContent = option.textContent.trim();

      self.state.sort = option.getAttribute('value');
      trigger.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
      self.apply();
    });

    document.addEventListener('click', function (event) {
      if (!sort.contains(event.target)) {
        trigger.setAttribute('aria-expanded', 'false');
        menu.hidden = true;
      }
    });
  };

  ShopAll.prototype.selectFacet = function (option) {
    var group = option.dataset.facet;
    if (group !== CATEGORY && group !== BRAND) return;

    var value = option.dataset.facetValue || '';
    this.state[group] = value;
    this.state.shown = this.batchSize;

    // Matched on the value, not on the node: one filter can be on screen twice — as a
    // sidebar row and as a pill — and both have to light up together.
    this.root.querySelectorAll('[data-facet="' + group + '"]').forEach(function (node) {
      node.classList.toggle('is-active', (node.dataset.facetValue || '') === value);
    });

    this.apply();
  };

  ShopAll.prototype.handleChip = function (chip) {
    if (chip.classList.contains('filter-chip--clear')) {
      this.clearAll();
      return;
    }

    var group = chip.dataset.chipGroup;
    var value = chip.dataset.chipValue;

    if (group === CATEGORY || group === BRAND) {
      var all = this.root.querySelector('[data-facet="' + group + '"][data-facet-value=""]');
      if (all) this.selectFacet(all);
      return;
    }

    if (group === 'size') {
      var input = this.root.querySelector('.checkbox__input[name="size"][value="' + value + '"]');
      if (input) {
        input.checked = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    if (group === 'price') {
      this.resetPrice();
    }
  };

  ShopAll.prototype.resetPrice = function () {
    if (!this.priceMin || !this.priceMax) return;
    this.priceMin.value = this.priceMin.min;
    this.priceMax.value = this.priceMax.max;
    this.state.priceMin = Number(this.priceMin.min);
    this.state.priceMax = Number(this.priceMax.max);
    this.updatePriceUI();
    this.apply();
  };

  ShopAll.prototype.clearAll = function () {
    var self = this;

    [CATEGORY, BRAND].forEach(function (group) {
      var all = self.root.querySelector('[data-facet="' + group + '"][data-facet-value=""]');
      if (!all) return;
      self.state[group] = '';
      self.root.querySelectorAll('[data-facet="' + group + '"]').forEach(function (node) {
        node.classList.toggle('is-active', node === all);
      });
    });

    this.root.querySelectorAll('.checkbox__input[name="size"]').forEach(function (input) {
      input.checked = false;
    });
    this.state.sizes = [];
    this.state.shown = this.batchSize;

    if (this.priceMin && this.priceMax) {
      this.priceMin.value = this.priceMin.min;
      this.priceMax.value = this.priceMax.max;
      this.state.priceMin = Number(this.priceMin.min);
      this.state.priceMax = Number(this.priceMax.max);
      this.updatePriceUI();
    }

    this.apply();
  };

  ShopAll.prototype.updatePriceUI = function () {
    if (!this.priceMin || !this.priceMax) return;

    var floor = Number(this.priceMin.min);
    var ceiling = Number(this.priceMin.max);
    var span = ceiling - floor || 1;
    var min = Number(this.priceMin.value);
    var max = Number(this.priceMax.value);

    if (this.priceFill) {
      this.priceFill.style.left = ((min - floor) / span) * 100 + '%';
      this.priceFill.style.width = ((max - min) / span) * 100 + '%';
    }
    if (this.priceReadoutMin) {
      this.priceReadoutMin.textContent = formatMoney(min, this.config.moneyFormat);
    }
    if (this.priceReadoutMax) {
      this.priceReadoutMax.textContent = formatMoney(max, this.config.moneyFormat);
    }
  };

  /* Terms arrive from Liquid as "oil~mist~", already lowercased. A product matches when
     its haystack contains any one of them padded with spaces, which is what keeps "siv"
     off "intensive". */
  ShopAll.prototype.matchesTerms = function (haystack, packedTerms) {
    if (!packedTerms) return true;
    var terms = packedTerms.split('~');
    for (var i = 0; i < terms.length; i++) {
      if (!terms[i]) continue;
      if (haystack.indexOf(' ' + terms[i] + ' ') !== -1) return true;
    }
    return false;
  };

  /* `skip` leaves one facet group out of the test. Passing null tests everything, which
     is what decides whether a card is shown; passing a group name gives that group the
     set it should be counting against — see updateCounts. */
  ShopAll.prototype.passes = function (item, skip) {
    var haystack = item.dataset.haystack || '';

    if (skip !== CATEGORY && !this.matchesTerms(haystack, this.state.category)) return false;
    if (skip !== BRAND && !this.matchesTerms(haystack, this.state.brand)) return false;

    if (skip !== 'price') {
      var price = Number(item.dataset.price);
      if (price < this.state.priceMin || price > this.state.priceMax) return false;
    }

    if (skip !== 'size' && this.state.sizes.length) {
      var sizes = (item.dataset.sizes || '').split('|').filter(Boolean);
      // A product with no size option is dropped too. Almost the whole catalogue is
      // single-variant, so letting the sizeless ones through made ticking a size look
      // like it did nothing — the handful of sized products vanished into ~120 others.
      var hit = sizes.some(function (size) {
        return this.state.sizes.indexOf(size) !== -1;
      }, this);
      if (!hit) return false;
    }

    return true;
  };

  ShopAll.prototype.matches = function (item) {
    return this.passes(item, null);
  };

  /* Every count is re-measured against the other active filters, so picking a brand
     drops the category numbers to what that brand actually has. A group never counts
     against its own selection: category counts ignore the chosen category, which is
     what lets you see the size of each alternative before switching to it. */
  ShopAll.prototype.updateCounts = function () {
    var self = this;

    [CATEGORY, BRAND].forEach(function (group) {
      var base = self.items.filter(function (item) { return self.passes(item, group); });

      self.root.querySelectorAll('[data-facet="' + group + '"]').forEach(function (option) {
        var terms = option.dataset.facetValue || '';
        var count = terms
          ? base.filter(function (item) {
              return self.matchesTerms(item.dataset.haystack || '', terms);
            }).length
          : base.length;

        var node = option.querySelector('.shop-all__facet-option-count');
        if (node) node.textContent = count;
        option.classList.toggle('is-empty', count === 0);
      });
    });

    var sizeBase = this.items.filter(function (item) { return self.passes(item, 'size'); });
    this.root.querySelectorAll('.shop-all__size').forEach(function (row) {
      var input = row.querySelector('.checkbox__input');
      if (!input) return;

      // "Products sold in this size" — the same set ticking it would show.
      var count = sizeBase.filter(function (item) {
        return (item.dataset.sizes || '').split('|').indexOf(input.value) !== -1;
      }).length;

      var node = row.querySelector('.checkbox__count');
      if (node) node.textContent = count;
      row.classList.toggle('is-empty', count === 0);
    });
  };

  ShopAll.prototype.sortItems = function (matched) {
    var sort = this.state.sort;

    return matched.slice().sort(function (a, b) {
      switch (sort) {
        case 'price-ascending':
          return Number(a.dataset.price) - Number(b.dataset.price);
        case 'price-descending':
          return Number(b.dataset.price) - Number(a.dataset.price);
        case 'title-ascending':
          return (a.dataset.title || '').localeCompare(b.dataset.title || '');
        case 'title-descending':
          return (b.dataset.title || '').localeCompare(a.dataset.title || '');
        default:
          return Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex);
      }
    });
  };

  ShopAll.prototype.columnCount = function () {
    if (!this.grid) return 4;
    var columns = window.getComputedStyle(this.grid).gridTemplateColumns;
    return columns ? columns.split(' ').filter(Boolean).length : 4;
  };

  ShopAll.prototype.apply = function () {
    this.matched = this.items.filter(this.matches, this);
    this.render();
    this.renderChips();
    this.updateCounts();
  };

  ShopAll.prototype.render = function () {
    var self = this;
    var ordered = this.sortItems(this.matched);

    /* The design hangs the product-of-the-month card under the first row rather than
       leaving it wherever the sort dropped it, so it is pulled out and spliced back in
       after one row's worth of cards. */
    var featured = null;
    if (this.featured && ordered.indexOf(this.featured) !== -1) {
      featured = this.featured;
      ordered = ordered.filter(function (item) { return item !== featured; });
    }

    var limit = Math.min(this.state.shown, ordered.length);
    var visible = ordered.slice(0, limit);

    if (featured) {
      visible.splice(Math.min(this.columnCount(), visible.length), 0, featured);
    }

    this.items.forEach(function (item) {
      item.hidden = visible.indexOf(item) === -1;
    });

    // One reflow for the whole reorder instead of one per card.
    var fragment = document.createDocumentFragment();
    visible.forEach(function (item) { fragment.appendChild(item); });
    ordered.slice(limit).forEach(function (item) { fragment.appendChild(item); });
    this.grid.appendChild(fragment);

    if (this.moreWrapper) {
      this.moreWrapper.hidden = limit >= ordered.length;
    }
    if (this.emptyMessage) {
      this.emptyMessage.hidden = visible.length > 0;
    }

    self.matchedCount = ordered.length + (featured ? 1 : 0);
  };

  ShopAll.prototype.buildChip = function (label, group, value) {
    var fragment = this.chipTemplate.content.cloneNode(true);
    var chip = fragment.querySelector('.filter-chip');
    chip.querySelector('.filter-chip__label').textContent = label;
    chip.dataset.chipGroup = group;
    if (value) chip.dataset.chipValue = value;
    return chip;
  };

  ShopAll.prototype.renderChips = function () {
    if (!this.chipsRow || !this.chipTemplate) return;

    var self = this;
    this.chipsRow.textContent = '';
    var any = false;

    [CATEGORY, BRAND].forEach(function (group) {
      if (!self.state[group]) return;
      var active = self.root.querySelector('[data-facet="' + group + '"].is-active');
      if (!active) return;
      // Sidebar rows keep the label in its own span; a pill is just the button's text.
      var label = active.querySelector('.shop-all__facet-option-label') || active;
      self.chipsRow.appendChild(self.buildChip(label.textContent.trim(), group));
      any = true;
    });

    this.state.sizes.forEach(function (size) {
      var input = self.root.querySelector('.checkbox__input[name="size"][value="' + size + '"]');
      var label = input && input.parentNode.querySelector('.checkbox__label');
      self.chipsRow.appendChild(self.buildChip(label ? label.textContent.trim() : size, 'size', size));
      any = true;
    });

    if (this.priceMin && this.priceMax) {
      var floor = Number(this.priceMin.min);
      var ceiling = Number(this.priceMax.max);
      if (this.state.priceMin > floor || this.state.priceMax < ceiling) {
        var text = formatMoney(this.state.priceMin, this.config.moneyFormat) +
          ' – ' + formatMoney(this.state.priceMax, this.config.moneyFormat);
        this.chipsRow.appendChild(this.buildChip(text, 'price'));
        any = true;
      }
    }

    if (any && this.chipClearTemplate) {
      this.chipsRow.appendChild(this.chipClearTemplate.content.cloneNode(true));
    }

    this.chipsRow.hidden = !any;
  };

  ShopAll.prototype.collapseSizes = function () {
    var rows = this.root.querySelectorAll('.shop-all__size');
    var keep = this.config.sizeVisibleCount || 7;
    if (rows.length <= keep) return;

    this.sizesCollapsed = true;
    Array.prototype.forEach.call(rows, function (row, index) {
      row.hidden = index >= keep;
    });
  };

  ShopAll.prototype.toggleSizes = function (button) {
    var rows = this.root.querySelectorAll('.shop-all__size');
    var keep = this.config.sizeVisibleCount || 7;
    this.sizesCollapsed = !this.sizesCollapsed;

    var collapsed = this.sizesCollapsed;
    Array.prototype.forEach.call(rows, function (row, index) {
      row.hidden = collapsed && index >= keep;
    });

    button.textContent = collapsed
      ? (this.config.seeAllLabel || 'see all')
      : (this.config.seeLessLabel || 'see less');
  };

  ShopAll.prototype.toggleDrawer = function (open) {
    if (!this.facets) return;
    this.facets.classList.toggle('is-open', open);
    // Locks the page behind the drawer so a scroll gesture moves the facets, not the grid.
    document.body.style.overflow = open ? 'hidden' : '';

    var trigger = this.root.querySelector('.shop-all__filter-toggle .dropdown__trigger');
    if (trigger) trigger.setAttribute('aria-expanded', String(open));
  };

  function init() {
    document.querySelectorAll('[data-shop-all]').forEach(function (root) {
      if (root.dataset.shopAllReady) return;
      root.dataset.shopAllReady = 'true';
      new ShopAll(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor tears a section down and re-renders it on every settings change.
  document.addEventListener('shopify:section:load', init);
})();
